import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { chatService } from '@/services/chatService';
import webRTCService from '@/services/webrtcService';
import {
  initializeWebSocket,
  disconnectWebSocket,
  subscribeToConversation,
  unsubscribeFromConversation,
  subscribeToUserChannel,
  unsubscribeFromUserChannel,
} from '@/services/websocketService';
import { toast } from 'sonner';

const ChatContext = createContext();

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export const ChatProvider = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  
  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  
  // Active conversation state
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  
  // Call state
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [callStatus, setCallStatus] = useState('idle'); // idle, calling, ringing, connected
  
  // WebSocket connected
  const [wsConnected, setWsConnected] = useState(false);
  
  // Refs
  const conversationChannelRef = useRef(null);
  const userChannelRef = useRef(null);

  // Get current user ID
  const getUserId = useCallback(() => {
    return user?.doctor?.id || user?.patient?.id || user?.id;
  }, [user]);

  // Initialize WebSocket when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      try {
        initializeWebSocket(token);
        setWsConnected(true);
        
        // Subscribe to user channel for calls
        const userId = getUserId();
        if (userId) {
          userChannelRef.current = subscribeToUserChannel(userId, {
            onIncomingCall: handleIncomingCall,
            onCallSignal: handleCallSignal,
          });
        }
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
        setWsConnected(false);
      }
    }

    return () => {
      if (userChannelRef.current) {
        unsubscribeFromUserChannel(getUserId());
      }
      disconnectWebSocket();
      setWsConnected(false);
    };
  }, [isAuthenticated, token, getUserId]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoadingConversations(true);
    try {
      const data = await chatService.getConversations();
      setConversations(data.data || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      // Don't show error toast - API might not be ready yet
    } finally {
      setLoadingConversations(false);
    }
  }, [isAuthenticated]);

  // Select/open a conversation
  const openConversation = useCallback(async (conversation) => {
    // Unsubscribe from previous conversation
    if (activeConversation && conversationChannelRef.current) {
      unsubscribeFromConversation(activeConversation.id);
    }

    setActiveConversation(conversation);
    setMessages([]);
    setLoadingMessages(true);

    try {
      // Fetch messages
      const data = await chatService.getMessages(conversation.id);
      setMessages((data.data || []).reverse()); // Reverse to show oldest first

      // Subscribe to conversation channel
      conversationChannelRef.current = subscribeToConversation(conversation.id, {
        onMessage: handleNewMessage,
        onMessageRead: handleMessageRead,
      });
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [activeConversation]);

  // Close active conversation
  const closeConversation = useCallback(() => {
    if (activeConversation && conversationChannelRef.current) {
      unsubscribeFromConversation(activeConversation.id);
    }
    setActiveConversation(null);
    setMessages([]);
  }, [activeConversation]);

  // Send a message
  const sendMessage = useCallback(async (content, type = 'text', metadata = null) => {
    if (!activeConversation) return;

    try {
      const message = await chatService.sendMessage(
        activeConversation.id,
        content,
        type,
        metadata
      );

      // Add message to local state
      setMessages(prev => [...prev, {
        ...message,
        sender_id: getUserId(),
        is_mine: true,
      }]);

      // Update conversation in list
      setConversations(prev => prev.map(conv => 
        conv.id === activeConversation.id
          ? { ...conv, last_message: { content, type, created_at: new Date() } }
          : conv
      ));

      return message;
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
      throw error;
    }
  }, [activeConversation, getUserId]);

  // Handle new message from WebSocket
  const handleNewMessage = useCallback((message) => {
    const userId = getUserId();
    const isMine = message.sender_id === userId;

    setMessages(prev => [...prev, { ...message, is_mine: isMine }]);

    // Update conversation list
    setConversations(prev => prev.map(conv =>
      conv.id === message.conversation_id
        ? {
            ...conv,
            last_message: { content: message.content, type: message.message_type, created_at: message.created_at },
            unread_count: isMine ? conv.unread_count : (conv.unread_count || 0) + 1,
          }
        : conv
    ));

    // Show notification if not the sender
    if (!isMine) {
      toast.info('New message received');
    }
  }, [getUserId]);

  // Handle message read event
  const handleMessageRead = useCallback((data) => {
    setMessages(prev => prev.map(msg =>
      data.messageIds.includes(msg.id) ? { ...msg, read_at: new Date() } : msg
    ));
  }, []);

  // Start a call
  const startCall = useCallback(async (callType = 'video') => {
    if (!activeConversation) return;

    try {
      setCallStatus('calling');
      
      webRTCService.setCallbacks({
        onRemoteStream: (stream) => {
          setRemoteStream(stream);
          setCallStatus('connected');
        },
        onConnected: () => setCallStatus('connected'),
        onError: (error) => {
          console.error('Call error:', error);
          toast.error('Call failed');
          endCall();
        },
        onClose: () => endCall(),
      });

      const { callId, localStream: stream } = await webRTCService.initiateCall(
        activeConversation.id,
        activeConversation.participant.id,
        callType
      );

      setActiveCall({
        id: callId,
        type: callType,
        participant: activeConversation.participant,
        isOutgoing: true,
      });
      setLocalStream(stream);
    } catch (error) {
      console.error('Failed to start call:', error);
      toast.error('Failed to start call');
      setCallStatus('idle');
    }
  }, [activeConversation]);

  // Handle incoming call
  const handleIncomingCall = useCallback((data) => {
    setIncomingCall({
      id: data.call_id,
      conversationId: data.conversation_id,
      type: data.call_type,
      caller: {
        id: data.caller_id,
        name: data.caller_name,
        image: data.caller_image,
      },
    });
    setCallStatus('ringing');
  }, []);

  // Answer incoming call
  const answerCall = useCallback(async () => {
    if (!incomingCall) return;

    try {
      webRTCService.setCallbacks({
        onRemoteStream: (stream) => {
          setRemoteStream(stream);
          setCallStatus('connected');
        },
        onConnected: () => setCallStatus('connected'),
        onError: (error) => {
          console.error('Call error:', error);
          endCall();
        },
        onClose: () => endCall(),
      });

      const { localStream: stream } = await webRTCService.answerCall(
        incomingCall.id,
        incomingCall.conversationId,
        incomingCall.caller.id,
        incomingCall.type
      );

      setActiveCall({
        id: incomingCall.id,
        type: incomingCall.type,
        participant: incomingCall.caller,
        isOutgoing: false,
      });
      setLocalStream(stream);
      setIncomingCall(null);
      setCallStatus('connected');
    } catch (error) {
      console.error('Failed to answer call:', error);
      toast.error('Failed to answer call');
      rejectCall();
    }
  }, [incomingCall]);

  // Reject incoming call
  const rejectCall = useCallback(async () => {
    if (incomingCall) {
      try {
        await webRTCService.rejectCall();
      } catch (error) {
        console.error('Error rejecting call:', error);
      }
    }
    setIncomingCall(null);
    setCallStatus('idle');
  }, [incomingCall]);

  // End active call
  const endCall = useCallback(async () => {
    try {
      await webRTCService.endCall();
    } catch (error) {
      console.error('Error ending call:', error);
    }
    setActiveCall(null);
    setLocalStream(null);
    setRemoteStream(null);
    setCallStatus('idle');
  }, []);

  // Handle call signaling
  const handleCallSignal = useCallback((data) => {
    if (data.signalType === 'call-ended' || data.signalType === 'call-rejected') {
      endCall();
      if (data.signalType === 'call-rejected') {
        toast.info('Call was rejected');
      }
    } else if (data.signalData) {
      webRTCService.handleSignal(data.signalData);
    }
  }, [endCall]);

  // Toggle video
  const toggleVideo = useCallback((enabled) => {
    webRTCService.toggleVideo(enabled);
  }, []);

  // Toggle audio
  const toggleAudio = useCallback((enabled) => {
    webRTCService.toggleAudio(enabled);
  }, []);

  // Create or get conversation with a user
  const startConversation = useCallback(async (participantId, participantType) => {
    try {
      const { id } = await chatService.getOrCreateConversation(participantId, participantType);
      await fetchConversations();
      return id;
    } catch (error) {
      console.error('Failed to start conversation:', error);
      toast.error('Failed to start conversation');
      throw error;
    }
  }, [fetchConversations]);

  const value = {
    // Conversations
    conversations,
    loadingConversations,
    fetchConversations,
    
    // Active conversation
    activeConversation,
    messages,
    loadingMessages,
    openConversation,
    closeConversation,
    sendMessage,
    startConversation,
    
    // Calls
    incomingCall,
    activeCall,
    localStream,
    remoteStream,
    callStatus,
    startCall,
    answerCall,
    rejectCall,
    endCall,
    toggleVideo,
    toggleAudio,
    
    // Connection status
    wsConnected,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
